const urlOkSchemes: ReadonlyArray<string> = ['http', 'https', 'mailto', 'tel'];
const svgNS = 'http://www.w3.org/2000/svg';
const xlinkNS = 'http://www.w3.org/1999/xlink';
const urlAllowImageData = false; // default: aus, data urls für Bilder verboten

/**
 * filters HTML based on allowedContentMode. allowedHtmlTags and allowedHtmlAttributes, 
 * filters and sanitizes style attributes,
 * sanitizes src, href, xlink:href attributes,
 * removes event handlers like onclick, onload, etc.
 * 
 * * replaces all Tags that are not allowed with p, but keeps the children and textnodes
 * * replaces font with span tags, keeps style attributes and moves color to style 
 * * replaces rgb with hex values in style attributes
 * * removes all attributes that are not allowed
 * * removes data-* attributes (except data-delayedsrc)
 * * removes all styles from style-attribute that are not allowed
 * * removes p Tag as only child (retaining children)
 * * removes html-comments
 * * removes trailing <br>
 * * sanitizes URLs in href, src, xlink:href attributes
 * * removes event handlers like onclick, onload, etc.
 */
export function filterHtml( content: string, mode: string = null, allowedHtmlTags: any = null, allowedHtmlAttributes: Array<string> = null ): string {
    const debugLog = false;

    if ( content == null || content == '' || typeof content === 'undefined' ) {
        if ( debugLog ) console.log( 'filterHTML: content is empty or undefined', content );
        return '';
    }
    if ( debugLog ) console.time( 'filterHTML' );

    // String und DOM-Parser Manipulation vor Browser-DOM 
    content = sanitizeHtml( content );

    if ( content == '' ) {
        if ( debugLog ) console.log( 'content is empty after sanitizing' );
        if ( debugLog ) console.timeEnd( 'filterHTML' );
        return '';
    }

    // String zu Browser DOM-Element umwandeln
    const rootElement = convertHTMLStringtoElement( content );

    if ( !rootElement ) {
        console.error( 'HTML is undefined' );
        return '';
    }

    let allowedContent = getAllowedContent( mode, allowedHtmlTags, allowedHtmlAttributes );
    // get default
    if ( typeof allowedContent === 'undefined' ) {
        console.info( 'HTML-Editor: AllowedContent not set. Using default allowedContent.' );
        allowedContent = getAllowedContent();
    }

    if ( debugLog ) console.group( 'filterHtml' );
    if ( debugLog ) console.log( 'filter HTML: ', rootElement, allowedContent );

    // removes <p>REMOVE</p> and empty style Attributes after DOM filter.
    function removeRemovedElements( string : string ) : string {
        return string.replace( /<p>REMOVE<\/p>/g, '' ).replace( /style=""/g, '');
    }
    // replace element with p element,
    // append children to new p element
    /** TODO
     * - replace inline elements with span and block elements with p
     */
    function replaceElement( element: Node ): Node {
        if ( element instanceof Element ) {
            if ( debugLog ) console.info( 'replace element tag', element.tagName );
        }
        
        let parser = new DOMParser().parseFromString( '', 'text/html' );
        //console.info( 'replace element', element.nodeType, element );

        // wenn leer oder style/script Tag oder kein Standard HTMLElement, komplett löschen
        if ( element.textContent.trim().length == 0
            || ( element instanceof Element && ( element.tagName === 'STYLE' || element.tagName === 'SCRIPT' ) )
            || isEmptyElement( element )
            || !isHTMLElement( element ) ) {
            /**
             * NICHT element während iteration entfernen, erzeugt Index-Shifts
             * Danach mit String Operation.
             */
            //element.parentNode.removeChild(element);
            const pElement = parser.createElement( 'p' );
            pElement.innerHTML = 'REMOVE';
            element.parentNode.replaceChild( pElement, element );
            return pElement;
        } else if ( element instanceof Element && element.tagName === 'FONT' ) {
            // font zu span umwandeln
            // Aktuelles style-Attribut vom font übernehmen
            let fontStyle = element.getAttribute( 'style' ) || '';
            let fontColor = element.getAttribute( 'color' ) ? `color:${element.getAttribute( 'color' )};` : '';

            // Kombiniere vorhandene Style-Angaben
            let combinedStyle = fontStyle + ( fontStyle && fontColor ? ' ' : '' ) + fontColor;
            // Erstelle ein neues span-Element
            let spanElement = parser.createElement( 'span' );

            if ( combinedStyle.trim() ) {
                spanElement.setAttribute( 'style', combinedStyle );
            }

            // Übertrage alle anderen Attribute außer color und style
            Array.from( element.attributes ).forEach( attr => {
                if ( attr.name !== 'color' && attr.name !== 'style' ) {
                    spanElement.setAttribute( attr.name, attr.value );
                }
            } );

            // Inhalte in das neue span verschieben
            while ( element.firstChild ) {
                spanElement.appendChild( element.firstChild );
            }

            element.replaceWith( spanElement );
            return spanElement;
        } else {
            const pElement = parser.createElement( 'p' );
            while ( element.firstChild ) {
                pElement.appendChild( element.firstChild );
            }
            element.parentNode.replaceChild( pElement, element );
            return pElement;
        }
    }

    function hasDataAttributes( element: Node ): boolean{
        return ( element instanceof HTMLElement && Object.keys( element.dataset ).length > 0);
    }

    function isEmptyElement( element: Node ): boolean {
        if ( element instanceof Element) {
            //console.log('check if element empty', element);
            const tagName = element.tagName.toLowerCase();
            // Nehme mögliche leere layout Element aus
            if ( element.hasAttribute( 'class' ) || hasDataAttributes( element ) ) {
                return false;
            }
            // Void elements die sichtbar sind
            if ( ['img', 'input', 'br', 'hr', 'area', 'base', 'col', 'embed', 'link', 'meta', 'param', 'source', 'track', 'wbr'].includes( tagName ) ) {
                return false;
            }
            // leere Tabellenzellen und -zeilen erhalten, sonst geht die Tabellenstruktur kaputt
            if ( ['tr', 'td', 'th'].includes( tagName ) ) {
                return false;
            }
        }
        let isEmpty: boolean = true;
        // Prüfe alle Kindknoten rekursiv
        Array.from( element.childNodes ).forEach( node => {
            // Textknoten: prüfe ob nicht-whitespace Inhalt vorhanden
            if ( node.nodeType === Node.TEXT_NODE ) {
                if ( node.textContent.trim() !== '' ) {
                    isEmpty = false;
                }
            }
            // Elementknoten
            else if ( node instanceof Element || node.nodeType === Node.ELEMENT_NODE ) {

                // Rekursive Prüfung für Kind-Elemente
                if ( !isEmptyElement( node ) ) {
                    isEmpty = false;
                }
            }
        } );
        //Element is empty
        //console.log('empty element found', element);
        return isEmpty;
    }

    function removeHTMLComments( elem: HTMLElement ): void {
        const walker = new DOMParser().parseFromString( '', 'text/html' ).createTreeWalker( elem, NodeFilter.SHOW_COMMENT, null );
        let commentNode;
        while ( ( commentNode = walker.nextNode() ) ) {
            commentNode.parentNode.removeChild( commentNode );
            // Reset the walker to start from the root again, since the DOM has changed
            walker.currentNode = elem;
        }
    }

    function cleanStyle( e: HTMLElement ): void {
        // Regulärer Ausdruck für erlaubte RGB-Werte
        // Regulärer Ausdruck für erlaubte RGB-Werte
        const rgbRegex = /rgb\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*\)/gi;
        const hexStyle = e.getAttribute( 'style' ).replace( rgbRegex, ( match, r, g, b ) => {
            const red = parseInt( r );
            const green = parseInt( g );
            const blue = parseInt( b );

            if ( red <= 255 && green <= 255 && blue <= 255 ) {
                return `#${red.toString( 16 ).padStart( 2, '0' )}${green.toString( 16 ).padStart( 2, '0' )}${blue.toString( 16 ).padStart( 2, '0' )}`;
            }
            return match; // Fallback: Original behalten wenn invalid
        } );

        const allowedStyles = allowedContent.styles;

        const styles = hexStyle.split( ';' ).map( style => style.trim() ).filter( Boolean );
        const cleanedStyles = styles.filter( style => {
            // remove unallowed keys and leys with unallowed value
            // nur am ersten ':' trennen, damit der komplette Wert geprüft wird (z.B. 'red:url(...)')
            const colonIdx = style.indexOf( ':' );
            if ( colonIdx === -1 ) {
                return false;
            }
            const key = style.slice( 0, colonIdx ).trim();
            const value = style.slice( colonIdx + 1 ).trim();
            const keyAllowed = allowedStyles === true ? true : (Array.isArray(allowedStyles) ? allowedStyles.includes( key ) : false);
            if ( debugLog ) console.log( 'key, value', [key, value] );
            // @ ' " \ / () & * and [] not allowed
            const unallowedValueChars = /.*[@`''\"\\\\/()&*\[\]].*/;
            // % without space or semicolon not allowed
            const unallowedProzent = /.*%[^\s;].*/;
            // special words
            const unallowedWords = /.*(behavior|behaviour|binding|expression|script|url|@import).*/i;


            // gültige rgb()-Werte sind oben bereits in hex umgewandelt,
            // übrig gebliebene rgb() sind ungültig und werden wie alle Klammern geblockt
            const valueAllowed = !( unallowedValueChars.test( value )
                || unallowedProzent.test( value )
                || unallowedWords.test( value ) );

            const allow = keyAllowed && valueAllowed;
            /*console.info(key + " keyAllowed?", keyAllowed);
            console.info(value + " valueAllowed?", valueAllowed)
            if (!allow) {
                console.info("remove style key", key);
            }*/
            return allow ? keyAllowed : false;
        } );
        if ( cleanedStyles.length ) {
            // set left over styles
            if ( debugLog ) console.info( 'setting style with cleanedStyles:', cleanedStyles );
            e.setAttribute( 'style', cleanedStyles.join( '; ' ) );
        } else {
            if ( debugLog ) console.info( 'all styles stripped removing style attribute' );
            // all styles have been stripped, remove style attribute
            e.removeAttribute( 'style' );
        }
    }
    function isHTMLElement( node: Node ): node is HTMLElement {
        return node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement;
    }
    function isSVGElement( node: Node ): node is HTMLElement {
        return node.nodeType === Node.ELEMENT_NODE && node instanceof SVGElement;
    }
    function isTextNode( node: Node ): node is Text {
        return node.nodeType === Node.TEXT_NODE;
    }
    function traverseAndClean( element: Node ): void {
        if ( debugLog ) console.log( 'traverseAndClean called with:', element.nodeType, element.nodeName, element );
        if ( isTextNode( element ) ) {
            // nothing wrong with textnodes and they do not have children. 
            // just return
            return;
        }
        if ((!isHTMLElement( element ) && !isSVGElement( element )) || isEmptyElement( element ) ) {
            // Elemente nur mit Whitespace (z.B. <span> </span> als Worttrenner) auspacken statt entfernen,
            // damit das Leerzeichen erhalten bleibt. Nur für HTML-Elemente, alles andere wird entfernt.
            if ( isHTMLElement( element ) && element.textContent.length > 0 && element.textContent.trim() === '' ) {
                const children = Array.from( element.childNodes );
                children.forEach( child => element.parentNode.insertBefore( child, element ) );
                element.parentNode.removeChild( element );
                // ausgepackte Kinder sind nicht mehr Teil der Iteration des Elternelements, daher hier prüfen
                children.forEach( child => traverseAndClean( child ) );
                return;
            }
            // alles was leer oder nicht HTML oder svg ist, direkt entfernen.
            element = replaceElement( element );
            return;
        }
        // if the element is a node, check if it should be replace
        const tagName = element.tagName.toLowerCase();
        
        // replace the element if it's not allowed
        if ( !( tagName in allowedContent.elements ) || allowedContent.elements[tagName] === false ) {
            // move possible child nodes into a p node that replaces the element
            // write new element back to element or childNOdes will ne be processed
            element = replaceElement( element );
        }
        if ( !isHTMLElement( element ) ) {
            return;
        }
        Array.from( element.attributes ).forEach( attr => {
            if ( debugLog ) console.log( tagName, attr );

            const attrAllowed = allowedContent.attributes === true ? true : (Array.isArray(allowedContent.attributes) ? allowedContent.attributes.includes( attr.name ) : false);
            if ( !allowedContent.attributes
                || !attrAllowed
                || ( attr.name.startsWith( 'data-' )
                    && !( attr.name === 'data-delayedsrc' ) ) ) {
                // data-delayedsrc is used in BIA portlets to lazy load images from superx 

                if ( debugLog ) console.info( 'remove attr.name', attr.name );
                element.removeAttribute( attr.name );

            } else if ( attr.name === 'style' && element.hasAttribute( 'style' ) ) {
                // Clean the style attribute
                cleanStyle( element );
            }

        } );

        sanitizeUrlAttributes( element );

        // clean the class attribute if classes are not allowed
        if ( !allowedContent.classes ) {
            element.removeAttribute( 'class' );
        }

        // traverse child nodes
        // Snapshot statt live NodeList, da Kinder während der Iteration ausgepackt werden können
        Array.from( element.childNodes ).forEach( ( child: any ) => {
            traverseAndClean( child );
        } );
    }

    // clean the attributes
    if ( debugLog ) console.log( 'allowed Attributes', allowedContent.attributes );
    if ( debugLog ) console.log( 'allowed Styles', allowedContent.styles );

    // remove HTML comments
    removeHTMLComments( rootElement );

    // start traversing and cleaning from the children of the root element,
    // the root container itself has no parent and must not be replaced
    Array.from( rootElement.childNodes ).forEach( child => traverseAndClean( child ) );
    const finalHtml = removeRemovedElements( rootElement.innerHTML );
    if ( debugLog ) console.log( 'filtered Html', finalHtml );
    if ( debugLog ) console.groupEnd();
    if ( debugLog ) console.timeEnd( 'filterHTML' );
    return finalHtml;
}

export function sanitizeHtml( htmlString: string ): string {
    function trimAndRemoveEmptyPTag( string: string ): string {
        // remove empty tags
        let value = ( string.trim() === '<p><br></p>' ) ? '' : removeTrailingBr( string.trim() );
        // remove empty p
        // remove empty br
        value = ( value === '<br>' ) ? '' : value;
        // fix Oberflächentextbearbeitung Special Word EMPTY
        value = ( value === '<p>EMPTY</p>' ) ? 'EMPTY' : value;
        return value;
    }

    function unwrapSingleParagraph( string: string ): string {
        const trimmed = string.trim();


        // check if string starts with <p> and ends with </p>
        if ( !trimmed.startsWith( '<p>' ) || !trimmed.endsWith( '</p>' ) ) {
            return string;
        }

        // strips the outer <p> and </p> tags
        const content = trimmed.slice( 3, -4 );

        // check if the content still contains <p> or </p> tags
        // should never happen since it is semantically incorrect HTML and editor/browser should fix it beforehand
        // but you never know...
        if ( content.includes( '<p>' ) || content.includes( '</p>' ) ) {
            // return full string
            return string;
        }

        // return unwrapped content
        return content;
    }

    function removeTrailingBr( string: string ): string {
        // replace trailing br and also trailing <p><br></p>
        return string.replace( /(<br\s*\/?>)+$/, '' ).replace( /<p>\s*<br>\s*<\/p>\s*$/g, '' );
    }

    function removeZeroWidthSpace( string: string ) {
        // remove zeroWithSpace control characters
        return string.trim().replace( /\u200B/g, '' );
    }

    htmlString = trimAndRemoveEmptyPTag( htmlString );
    if ( htmlString === '' ) return '';

    htmlString = removeZeroWidthSpace( htmlString );
    htmlString = unwrapSingleParagraph( htmlString );
    return htmlString;//.replace( /\n\s*/g, '' ); // potentielle Zeilenumbrüche + Leerzeichen vom codemirror formatter entfernen;
}

function convertHTMLStringtoElement( htmlString: string ): HTMLElement {
    const parsed = new DOMParser().parseFromString( '', 'text/html' );
    if ( !htmlString || typeof htmlString !== 'string' ) return parsed.createElement( 'div' );

    
    const tempContainer = parsed.createElement( 'div' );

    //console.log( 'HTML vor innerHTML:', htmlString );
    tempContainer.innerHTML = htmlString?.trim();
    //console.log( 'HTML nach innerHTML:', tempContainer.innerHTML );
    
    return tempContainer;
}

export function getAllowedContent( mode: string = null, allowedHtmlTags: any = null, allowedHtmlAttributes: Array<string> = null ): any {
    let allowedContent;
    switch ( mode ) {
        case 'extendedHtml':
            allowedContent = {
                // Use the ability to specify elements as an object.
                elements: allowedHtmlTags,
                attributes: allowedHtmlAttributes,
                styles: true,
                classes: true
            };
            break;
        case 'tagsOnly':
            allowedContent = {
                // Use the ability to specify elements as an object.
                // Tags only
                elements: { h1: false, h2: true, h3: true, h4: true, h5: true, h6: true, a: true, abbr: true, b: true, i: true, u: true, sub: true, sup: true, ul: true, ol: true, li: true, dl: true, dt: true, dd: true, p: true, span: true, em: true, s: true, strike: true, strong: true, table: true, tbody: true, thead: true, tfoot: true, tr: true, th: true, td: true, colgroup: true, col: true, caption: true, div: true, small: true, pre: true, code: true, br: true, hr: true, img: true, style: false, article: true, section: true, header: true, footer: true, main: true, nav: true, aside: true },
                attributes: false,
                styles: false,
                classes: false
            };
            break;
        case 'onPaste':
            allowedContent = {
                // Use the ability to specify elements as an object.
                // filter some styles on Paste only
                elements: { h1: false, h2: true, h3: true, h4: true, h5: true, h6: true, a: true, abbr: true, b: true, i: true, u: true, sub: true, sup: true, ul: true, ol: true, li: true, dl: true, dt: true, dd: true, p: true, span: true, em: true, s: true, strike: true, strong: true, table: true, tbody: true, thead: true, tfoot: true, tr: true, th: true, td: true, colgroup: true, col: true, caption: true, div: true, small: true, pre: true, code: true, br: true, hr: true, img: true, style: false, article: true, section: true, header: true, footer: true, main: true, nav: true, aside: true },
                attributes: ['aria-label', 'aria-labelledby', 'aria-describedby', 'title', 'alt', 'src', 'target', 'style', 'class', 'align', 'valign', 'bgcolor', 'colspan', 'rowspan', 'height', 'nowrap', 'width', 'cellpadding', 'cellspacing', 'border', 'id', 'scope', 'summary', 'href', 'lang', 'data-delayedsrc'],
                styles: ['width', 'height', 'direction', 'list-style-type', 'list-style-position', 'color', 'background-color', 'text-decoration', 'border', 'border-top', 'border-right', 'border-bottom', 'border-left', 'border-width', 'border-style', 'border-color', 'border-collapse'],
                classes: false
            };
            break;
        default:
            allowedContent = {
                // Use the ability to specify elements as an object.
                // allowed tags, attributes and styles, from HTMLUtil.java, keep in sync!
                // font and font-family is allowed in HTMLUtil.java, but ' and " in style attribute aren't.
                // therefore filter font and font-family, since there is no value in getting the font-family from a Word Document, because the texts are always within a font-family setting context (Portal, FOP-Report, LO-Report) 
                elements: { h1: false, h2: true, h3: true, h4: true, h5: true, h6: true, a: true, abbr: true, b: true, i: true, u: true, sub: true, sup: true, ul: true, ol: true, li: true, dl: true, dt: true, dd: true, p: true, span: true, em: true, s: true, strike: true, strong: true, table: true, tbody: true, thead: true, tfoot: true, tr: true, th: true, td: true, colgroup: true, col: true, caption: true, div: true, small: true, pre: true, code: true, br: true, hr: true, img: true, style: false, article: true, section: true, header: true, footer: true, main: true, nav: true, aside: true },
                attributes: ['aria-label', 'aria-labelledby', 'aria-describedby', 'title', 'alt', 'src', 'target', 'style', 'class', 'align', 'valign', 'bgcolor', 'colspan', 'rowspan', 'height', 'nowrap', 'width', 'cellpadding', 'cellspacing', 'border', 'id', 'scope', 'summary', 'href', 'lang', 'data-delayedsrc'],
                styles: ['width', 'height', 'direction', 'line-height', 'vertical-align', 'list-style-type', 'list-style-position', 'color', 'background-color', 'background-repeat', 'font-style', 'font-variant', 'font-weight', 'font-size', 'text-indent', 'text-align', 'text-decoration', 'letter-spacing', 'word-spacing', 'text-transform', 'white-space', 'border', 'border-top', 'border-right', 'border-bottom', 'border-left', 'border-width', 'border-style', 'border-color', 'border-collapse'],
                classes: true
            };
    }
    return allowedContent;
}

// === start url-sanitize helpers ===
function urlStripProblemAttrs( el: Element ): void {
    const ban = ['srcdoc', 'formaction', 'action', 'poster', 'background'];
    for ( const name of ban ) {
        if ( el.hasAttribute( name ) ) el.removeAttribute( name );
    }
}

function urlStripCtrlChars( s: string ): string {
    if ( !s ) return '';
    let out = '';
    for ( let i = 0; i < s.length; i++ ) {
        const c = s.charCodeAt( i );
        if ( ( c >= 0x20 && c !== 0x7F ) || c > 0x9F ) out += s.charAt( i );
    }
    return out;
}

function urlClean( s: string ): string {
    if ( !s ) return '';
    s = urlStripCtrlChars( s ).trim();
    if ( ( s.startsWith( '"' ) && s.endsWith( '"' ) ) || ( s.startsWith( '\'' ) && s.endsWith( '\'' ) ) ) {
        s = s.slice( 1, -1 ).trim();
    }
    return s;
}

function urlHardenAnchor( el: Element ): void {
    if ( el.tagName.toLowerCase() !== 'a' ) return;
    const target = el.getAttribute( 'target' );
    if ( target && target.toLowerCase() === '_blank' ) {
        const rel = new Set( ( el.getAttribute( 'rel' ) || '' ).split( /\s+/ ).filter( Boolean ) );
        rel.add( 'noopener' ); rel.add( 'noreferrer' ); rel.add( 'ugc' );
        el.setAttribute( 'rel', Array.from( rel ).join( ' ' ) );
    }
}

function urlGetScheme( s: string ): string | null {
    const m = /^\s*([a-z0-9+.-]+):/i.exec( s );
    return m ? m[1].toLowerCase() : null;
}

// prüft, ob die URL ein Anker ist, also nur ein Hash (#) und evtl. ein Name
function urlIsHashOnly( s: string ): boolean {
    return /^#[\w\-:.~%]+$/.test( s );
}

// href ohne gültiges Protokoll blocken (relative URLs und anchors sind auch ok)
export function urlSanitizeHref( raw: string ): string | null {
    let v = urlClean( raw );
    if ( !v ) return null;

    // anchor erlauben
    if ( urlIsHashOnly( v ) ) return v;

    // protokoll prüfen (relative/proto-relativ haben keines, auch ok)
    const scheme = urlGetScheme( v );
    if ( !scheme ) return v;

    // nur explizit erlaubte schemata durchlassen
    if ( !urlOkSchemes.includes( scheme ) ) return null;

    return v;
}

// src: unerwünschte Schemas blocken
// erlaubt: relative, //host, http, https, mailto, tel
export function urlSanitizeSrc( raw: string, tagName: string ): string | null {
    let v = urlClean( raw );
    if ( !v ) return null;

    const tag = ( tagName || '' ).toLowerCase();

    // data: speziell behandeln (aktuell immer blocken)
    if ( v.toLowerCase().startsWith( 'data:' ) ) {
        if ( tag !== 'img' || !urlAllowImageData ) return null; // nur img darf eventuell data:
        if ( !/^data:image\/[a-z0-9+.-]+;base64,[a-z0-9+/=\s]+$/i.test( v ) ) return null;
        if ( /^data:image\/svg\+xml/i.test( v ) ) return null; // kein data bei svg
        return v;
    }

    const scheme = urlGetScheme( v );
    if ( !scheme ) return v; // relative und // host auch ok

    if ( !urlOkSchemes.includes( scheme ) ) return null;

    return v;
}

// srcset wie src für jede url im set
export function urlSanitizeSrcset( raw: string, tagName: string ): string | null {
    // nur an Komma + Whitespace trennen, Kommas innerhalb von URLs (z.B. data:) nicht
    const parts = ( raw || '' ).split( /,\s+/ ).map( x => x.trim() ).filter( Boolean );
    if ( !parts.length ) return null;
    const out: string[] = [];
    for ( const p of parts ) {
        const m = /^(\S+)(\s+\d+(w|x))?$/.exec( p );
        if ( !m ) continue;
        const url = urlSanitizeSrc( m[1], tagName );
        if ( url ) out.push( url + ( m[2] || '' ) );
    }
    return out.length ? out.join( ', ' ) : null;
}

// Inline Eventhandler, on* attribute, entfernen
function urlStripEventHandlers( el: Element ): void {
    const names = ( el as any ).getAttributeNames
        ? ( el as any ).getAttributeNames() as string[]
        : Array.from( el.attributes ).map( a => a.name );
    for ( const name of names ) {
        if ( name.toLowerCase().startsWith( 'on' ) ) {
            el.removeAttribute( name );
        }
    }
}

// svg: xlink:href entfernen (Namespace + Fallback)
function urlRemoveXlinkHref( el: Element ): void {
    if ( el.hasAttributeNS( xlinkNS, 'href' ) ) {
        el.removeAttributeNS( xlinkNS, 'href' );
    }
    if ( el.hasAttribute( 'xlink:href' ) ) {
        el.removeAttribute( 'xlink:href' );
    }
}

// ein Element sanitizen
function urlSanitizeSingleElement( el: Element ): void {
    const tag = el.tagName.toLowerCase();
    const isSvg = el.namespaceURI === svgNS;

    // SVG2: unnamespaced href
    if ( isSvg && el.hasAttribute( 'href' ) ) {
        const hv = urlSanitizeHref( el.getAttribute( 'href' ) || '' );
        if ( hv ) el.setAttribute( 'href', hv ); else el.removeAttribute( 'href' );
    }

    // SVG1.1: xlink:href sanitizen
    let xv: string | null = null;
    const hadNS = el.hasAttributeNS( xlinkNS, 'href' );
    if ( hadNS ) {
        xv = el.getAttributeNS( xlinkNS, 'href' );
    } else if ( el.hasAttribute( 'xlink:href' ) ) {
        xv = el.getAttribute( 'xlink:href' );
    }
    if ( xv != null ) {
        const clean = urlSanitizeHref( xv );
        if ( clean ) {
            if ( hadNS ) {
                el.setAttributeNS( xlinkNS, 'href', clean );
            } else {
                el.setAttribute( 'xlink:href', clean );
            }
        } else {
            // bei unzulässigem Wert entfernen
            urlRemoveXlinkHref( el );
        }
    }

    // normale HTML-Attribute
    if ( el.hasAttribute( 'href' ) ) {

        urlHardenAnchor( el );
        const v = urlSanitizeHref( el.getAttribute( 'href' ) || '' );
        if ( v ) el.setAttribute( 'href', v ); else el.removeAttribute( 'href' );
    }
    if ( el.hasAttribute( 'src' ) ) {
        const v = urlSanitizeSrc( el.getAttribute( 'src' ) || '', tag );
        if ( v ) el.setAttribute( 'src', v ); else el.removeAttribute( 'src' );
    }
    if ( tag === 'img' && el.hasAttribute( 'srcset' ) ) {
        const v = urlSanitizeSrcset( el.getAttribute( 'srcset' ) || '', tag );
        if ( v ) el.setAttribute( 'srcset', v ); else el.removeAttribute( 'srcset' );
    }

    // Event-Handler streichen
    urlStripEventHandlers( el );
    urlStripProblemAttrs( el );
}

// url attribute sanitization helper
export function sanitizeUrlAttributes( el: Element ): void {
    const isSvgRootOrNode = el.namespaceURI === svgNS;

    if ( !isSvgRootOrNode ) {
        // normales Element: nur dieses Element bearbeiten
        urlSanitizeSingleElement( el );
        return;
    }

    // SVG: dieses Element + alle Kindelemente bearbeiten
    urlSanitizeSingleElement( el );

    // performant & namespace-agnostisch: TreeWalker über Elemente
    const walker = new DOMParser().parseFromString( '', 'text/html' ).createTreeWalker( el, NodeFilter.SHOW_ELEMENT );
    for ( let n = walker.nextNode() as Element | null; n; n = walker.nextNode() as Element | null ) {
        urlSanitizeSingleElement( n );
    }
}
// === ende url-sanitize helpers ===

/**
 * Shortens a text to a maximum length, ensuring that it does not cut off in the middle of a word.
 * If the text exceeds the maximum length, it will be truncated and '...' will be appended.
 * @param text The HTML content to shorten.
 * @param maxLength The maximum length of the text.
 * @returns The shortened HTML content.
 */
export function shortenHtmlByContentMaxLimit( text: string, maxLength: number ): string {

    function shortenNodeContent( node: Node, allowedLength: number ): number {
        if ( allowedLength <= 0 ) {
            // Wenn die maximale Länge erreicht ist, entferne den Knoten
            node.parentNode?.removeChild( node );
            return 0;
        }

        if ( node.nodeType === Node.TEXT_NODE ) {
            const nodeText = node.textContent || '';
            if ( nodeText.length > allowedLength ) {
                node.textContent = nodeText.slice( 0, allowedLength ) + ' ...';
                return 0;
            } else {
                return allowedLength - nodeText.length;
            }
        }

        if ( node.nodeType === Node.ELEMENT_NODE ) {
            const children = Array.from( node.childNodes );
            for ( const child of children ) {
                allowedLength = shortenNodeContent( child, allowedLength );
            }
        }

        return allowedLength;
    }

    let tParser = new DOMParser().parseFromString( '', 'text/html' ).createElement( 'div' );
    tParser.innerHTML = text;
    shortenNodeContent( tParser, maxLength );
    return ( tParser.innerHTML || '' ).trim();
}